import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Avatar,
  ListItemText,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Person,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { clearKeepAliveCache } from './KeepAliveOutlet';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/useI18n';
import type { AppLang } from '../../i18n';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { lang, setLang, langs, langLabels, langShort, langFlags, pages } = useI18n();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLang = (next: AppLang) => {
    setLang(next);
    handleLangMenuClose();
  };

  const hideBack =
    location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname === '/register';

  const handleBack = () => {
    if (location.pathname === '/ai-themes') {
      const event = new CustomEvent('ai-themes-back', { cancelable: true });
      if (!window.dispatchEvent(event)) return;
    }
    const idx = Number(window.history.state?.idx || 0);
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  const backButtonSx = {
    color: '#ff8ba7',
    bgcolor: '#fff',
    width: { xs: 34, sm: 36 },
    height: { xs: 34, sm: 36 },
    mr: { xs: 0.25, md: 0.75 },
    boxShadow: '0 2px 8px rgba(0,0,0,0.16)',
    '&:hover': { bgcolor: '#fff8fa' },
  } as const;

  const renderBackButton = () =>
    hideBack ? null : (
      <IconButton
        onClick={handleBack}
        aria-label="Quay lại trang trước"
        sx={backButtonSx}
      >
        <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
      </IconButton>
    );

  const openLogoutDialog = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      clearKeepAliveCache();
      await dispatch(logout()).unwrap();
      setLogoutDialogOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  const adminMenuItems: Array<{ title: string; path: string; icon: React.ReactNode }> = [];

  const staffMenuItems: Array<{ title: string; path: string; icon: React.ReactNode }> = [];

  const userMenuItems: Array<{ title: string; path: string; icon: React.ReactNode }> = [];

  const getMenuItems = () => {
    switch (user?.role) {
      case 'ADMIN':
        return adminMenuItems;
      case 'STAFF':
        return staffMenuItems;
      default:
        return userMenuItems;
    }
  };

  const menuItems = getMenuItems();

  return (
    <>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          backgroundColor: '#ff8ba7',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              position: 'relative',
              minHeight: { xs: 56, sm: 64 },
            }}
          >
            {/* Logo + brand — desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
              {renderBackButton()}
              {hideBack && (
                <Box
                  component={Link}
                  to="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                  }}
                  sx={{ display: 'flex', lineHeight: 0 }}
                >
                  <img src="/m-photo-logo.png" alt="M-Photo" style={{ height: 40 }} />
                </Box>
              )}
            </Box>
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: '#fff',
                textDecoration: 'none',
                cursor: 'pointer',
                textShadow: '0 1px 2px rgba(142, 77, 97, 0.25)',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                '&:hover, &:focus, &:active, &:visited, &:focus-visible': {
                  color: '#fff',
                  textDecoration: 'none',
                  backgroundColor: 'transparent',
                  opacity: 1,
                  outline: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              M-PHOTO
            </Typography>

            {/* Mobile menu (only when there are items) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', zIndex: 1, gap: 0.25 }}>
              {renderBackButton()}
              {hideBack && (
                <Box
                  component={Link}
                  to="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 0,
                    textDecoration: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src="/m-photo-logo.png"
                    alt="M-Photo"
                    sx={{ height: 36, width: 36, borderRadius: '50%', objectFit: 'cover' }}
                  />
                </Box>
              )}
              {menuItems.length > 0 && (
                <>
                  <IconButton
                    size="large"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMobileMenuOpen}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={mobileMoreAnchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    open={Boolean(mobileMoreAnchorEl)}
                    onClose={handleMobileMenuClose}
                    sx={{
                      display: { xs: 'block', md: 'none' },
                    }}
                  >
                    {menuItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          handleMobileMenuClose();
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.icon}
                          <Typography sx={{ ml: 1 }}>{item.title}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>

            {/* Brand — mobile, centered in navbar */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{
                display: { xs: 'flex', md: 'none' },
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: '#fff',
                textDecoration: 'none',
                zIndex: 2,
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                textShadow: '0 1px 2px rgba(142, 77, 97, 0.25)',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                '&:hover, &:focus, &:active, &:visited, &:focus-visible': {
                  color: '#fff',
                  textDecoration: 'none',
                  backgroundColor: 'transparent',
                  opacity: 1,
                  outline: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              M-PHOTO
            </Typography>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    my: 2,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  startIcon={item.icon}
                >
                  {item.title}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: { xs: 0.75, sm: 1 },
                zIndex: 1,
                position: 'relative',
              }}
            >
              <Button
                size="small"
                onClick={handleLangMenuOpen}
                aria-label={langLabels[lang]}
                aria-controls={langAnchorEl ? 'lang-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(langAnchorEl) ? 'true' : undefined}
                startIcon={
                  <Box
                    component="img"
                    src={langFlags[lang]}
                    alt=""
                    sx={{
                      width: { xs: 24, sm: 22 },
                      height: { xs: 16, sm: 15 },
                      objectFit: 'cover',
                      borderRadius: '2px',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                    }}
                  />
                }
                sx={{
                  minWidth: { xs: 40, sm: 0 },
                  px: { xs: 0.85, sm: 1.5 },
                  py: 0.55,
                  textTransform: 'none',
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  color: '#8E4D61',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(255,255,255,0.9)',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  '&:hover': {
                    backgroundColor: '#fff8fa',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.22)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 0.75 },
                    ml: 0,
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {langShort[lang]}
                </Box>
              </Button>
              <Menu
                id="lang-menu"
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                PaperProps={{
                  sx: { minWidth: 200, mt: 0.5 },
                }}
              >
                {langs.map((code) => (
                  <MenuItem
                    key={code}
                    selected={code === lang}
                    onClick={() => handleSelectLang(code)}
                    sx={{ py: 1.1, gap: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={langFlags[code]}
                      alt=""
                      sx={{
                        width: 28,
                        height: 19,
                        objectFit: 'cover',
                        borderRadius: '3px',
                        flexShrink: 0,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                      }}
                    />
                    <ListItemText
                      primary={langLabels[code]}
                      secondary={langShort[code]}
                      sx={{ ml: 1 }}
                    />
                    {code === lang ? (
                      <CheckIcon fontSize="small" sx={{ color: '#8E4D61', ml: 1 }} />
                    ) : null}
                  </MenuItem>
                ))}
              </Menu>

              <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                <Avatar
                  alt={user?.username}
                  src={user?.avatarUrl || '/static/images/avatar/2.jpg'}
                />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/profile');
                    handleMenuClose();
                  }}
                >
                  <Person sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={openLogoutDialog}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Spacer — AppBar fixed nên cần chừa chỗ */}
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />

      <Dialog
        open={logoutDialogOpen}
        onClose={() => {
          if (!loggingOut) setLogoutDialogOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            color: 'common.white',
            background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 55%, #f87171 100%)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Xác nhận đăng xuất
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <DialogContentText sx={{ color: 'text.primary', fontWeight: 600 }}>
            Bạn có chắc muốn đăng xuất khỏi tài khoản này?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            disabled={loggingOut}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
          >
            {pages.common.cancel}
          </Button>
          <Button
            onClick={() => void handleLogout()}
            variant="contained"
            color="error"
            disabled={loggingOut}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, px: 2.25 }}
          >
            {loggingOut ? 'Đang đăng xuất…' : 'Đăng xuất'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar; 